import React, { useEffect, useState } from 'react';
import { X, Copy, CheckCircle2, Download } from 'lucide-react';
import upiQr from '../assets/optimized/upi-qr.jpg';

const BookingModal = ({ isOpen, onClose, service }) => {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.classList.add('modal-open');
        } else {
            document.body.style.overflow = 'unset';
            document.body.classList.remove('modal-open');
        }
        return () => {
            document.body.style.overflow = 'unset';
            document.body.classList.remove('modal-open');
        };
    }, [isOpen]);

    const handleCopy = () => {
        navigator.clipboard.writeText('manjunathajpnagar6th-1@oksbi');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDownloadQr = () => {
        const link = document.createElement('a');
        link.href = upiQr;
        link.download = 'massage-service-qr.jpg';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (!isOpen || !service) return null;

    const message = `Hi, I'm interested in booking:\n\n*${service.name}*\nDuration: ${service.duration}\nPrice: ${service.price}\n\nI have paid the ₹500 advance. Please confirm my slot.`;

    const whatsappLink = `https://wa.me/919901853969?text=${encodeURIComponent(message)}`;

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            ></div>

            {/* Modal Content */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-fade-in-up flex flex-col max-h-[90vh]">
                {/* Header - Fixed */}
                <div className="bg-zen-gold p-4 text-center relative shrink-0 rounded-t-2xl">
                    <button
                        onClick={onClose}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/10 hover:bg-black/20 rounded-full text-white transition-colors"
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                    <h3 className="text-xl font-display font-bold text-white">Confirm Booking</h3>
                </div>

                {/* Scrollable Body */}
                <div className="p-6 overflow-y-auto">
                    {/* Sympathetic Note */}
                    <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 mb-6 text-sm text-coffee-700 leading-relaxed shadow-sm">
                        <p className="font-semibold text-orange-800 mb-1">Dear Customer,</p>
                        We travel 10-20km to provide home service. Last-minute cancellations after travelling cause us great loss and we have to return empty-handed. <br /><br />
                        To confirm your genuine interest and cover our travel costs, we kindly request a fully adjustable advance of <strong className="text-coffee-900">₹500</strong>.
                    </div>

                    {/* QR Code Section */}
                    <div className="flex flex-col items-center mb-6">
                        <div className="w-48 h-48 bg-gray-100 rounded-xl overflow-hidden shadow-lg border-2 border-dashed border-zen-gold/50 mb-3 relative group">
                            <img
                                src={upiQr}
                                alt="UPI QR Code"
                                className="w-full h-full object-contain p-2"
                            />
                        </div>
                        <p className="text-xs text-center text-gray-500 mb-2">Scan to pay with any UPI app</p>

                        {/* UPI Copy */}
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 w-full justify-between">
                            <span className="text-xs font-mono text-gray-600 truncate">manjunathajpnagar6th-1@oksbi</span>
                            <button
                                onClick={handleCopy}
                                className="text-zen-gold hover:text-coffee-900 transition-colors"
                            >
                                {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                            </button>
                        </div>
                    </div>

                    {/* Direct Payment Button */}
                    <div className="mb-6">
                        <a
                            href={`upi://pay?pa=manjunathajpnagar6th-1@oksbi&pn=Healthy Massage&am=500&tn=Booking Advance - ${service?.name || 'Service'}`}
                            className="block w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl text-center"
                        >
                            <div className="flex items-center justify-center gap-3">
                                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z" />
                                </svg>
                                <span>Pay ₹500 Advance via UPI</span>
                            </div>
                        </a>
                        <p className="text-xs text-center text-gray-500 mt-2">Choose from PhonePe, Google Pay, Paytm, or any UPI app</p>
                    </div>

                    <div className="relative mb-4">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300"></div>
                        </div>
                        <div className="relative flex justify-center text-xs">
                            <span className="bg-white px-2 text-gray-500">Or scan QR code</span>
                        </div>
                    </div>

                    {/* Download QR */}
                    <button
                        onClick={handleDownloadQr}
                        className="w-full mb-3 flex items-center justify-center gap-2 text-sm text-zen-gold hover:text-coffee-900 font-semibold transition-colors bg-zen-gold/5 px-4 py-2 rounded-lg hover:bg-zen-gold/10"
                    >
                        <Download size={16} />
                        Download QR Code
                    </button>


                    {/* Steps */}
                    <div className="space-y-3 mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-zen-gold text-white flex items-center justify-center text-xs font-bold shrink-0">1</div>
                            <span className="text-sm text-coffee-700">Pay <strong>₹500</strong> using above QR</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-zen-gold text-white flex items-center justify-center text-xs font-bold shrink-0">2</div>
                            <span className="text-sm text-coffee-700">Take a screenshot of payment</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-zen-gold text-white flex items-center justify-center text-xs font-bold shrink-0">3</div>
                            <span className="text-sm text-coffee-700">Share screenshot on WhatsApp to confirm</span>
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="pb-2">
                        <a
                            href={whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-3.5 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl animate-pulse-gentle"
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                            </svg>
                            Share Payment on WhatsApp
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookingModal;
